import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Select } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
	ChoiceFollowUpAction,
	FOLLOW_UP_ACTION_OPTIONS,
	getFollowUpActionLabels,
	SITE_SURVEY_ANSWER_TYPE,
	taskLevelFollowUpActionsExist,
} from '../../utils/SiteSurveyUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { CustomCellProps } from './ImportGridTypes';

interface MultipleChoice {
	orderingId: number;
	value: string;
	description?: string;
	followUpActions?: ChoiceFollowUpAction[];
}

const renumber = (items: MultipleChoice[]): MultipleChoice[] =>
	items.map((item, orderingId) => ({ ...item, orderingId }));

const formatChoiceLabel = (choice: MultipleChoice): string => {
	const base = choice.description ? `${choice.value}: ${choice.description}` : choice.value;
	const actionLabels = getFollowUpActionLabels(choice.followUpActions);
	// Choices are joined with "; " in the cell summary, so separate the
	// follow-up labels with ", " inside the brackets to avoid ambiguity.
	return actionLabels.length > 0 ? `${base} [${actionLabels.join(', ')}]` : base;
};

const normalizeChoices = (value: any): MultipleChoice[] => {
	if (Array.isArray(value)) {
		return renumber(
			value
				.filter((item) => item && typeof item.value === 'string')
				.map((item) => ({
					orderingId: 0,
					value: item.value,
					...(item.description && { description: item.description }),
					...(Array.isArray(item.followUpActions) && item.followUpActions.length > 0
						? { followUpActions: item.followUpActions }
						: {}),
				}))
		);
	}
	// Legacy CSV path — the grid hands us a string like "yes:Yes,no:No" before
	// the user has touched the cell. Split into the same shape so the modal
	// renders existing choices.
	if (typeof value === 'string' && value.trim().length > 0) {
		return renumber(
			value
				.split(',')
				.map((entry) => {
					const trimmed = entry.trim();
					const colonIdx = trimmed.indexOf(':');
					if (colonIdx === -1) return { orderingId: 0, value: trimmed };
					const optionValue = trimmed.substring(0, colonIdx).trim();
					const description = trimmed.substring(colonIdx + 1).trim();
					return description.length > 0
						? { orderingId: 0, value: optionValue, description }
						: { orderingId: 0, value: optionValue };
				})
				.filter((choice) => choice.value.length > 0)
		);
	}
	return [];
};

const hasNonEmptyValue = (item: MultipleChoice): boolean =>
	typeof item.value === 'string' && item.value.trim().length > 0;

interface ChoiceRowProps {
	item: MultipleChoice;
	index: number;
	followUpActionsDisabled: boolean;
	onUpdate: (index: number, next: Partial<MultipleChoice>) => void;
	onDelete: (index: number) => void;
	onEnterKey: () => void;
}

const ChoiceRow: FC<ChoiceRowProps> = ({
	item,
	index,
	followUpActionsDisabled,
	onUpdate,
	onDelete,
	onEnterKey,
}) => {
	const handleValueChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onUpdate(index, { value: event.target.value });
		},
		[index, onUpdate]
	);

	const handleDescriptionChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onUpdate(index, { description: event.target.value });
		},
		[index, onUpdate]
	);

	const handleFollowUpActionsChange = useCallback(
		(nextActions: ChoiceFollowUpAction[]) => {
			onUpdate(index, {
				followUpActions: Array.isArray(nextActions) && nextActions.length > 0 ? nextActions : [],
			});
		},
		[index, onUpdate]
	);

	const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);

	// Only the freshly-mounted row whose value+description are both empty wants
	// focus. Antd Input's autoFocus only fires on mount, so existing rows are
	// unaffected even though the predicate would evaluate true if cleared later.
	const shouldAutoFocus = !item.value && !item.description;

	return (
		<div
			className="group mb-2 grid items-center gap-2"
			style={{ gridTemplateColumns: '1fr 1fr 1fr 24px' }}
		>
			<Input
				placeholder="Score"
				value={item.value}
				onChange={handleValueChange}
				onPressEnter={onEnterKey}
				autoFocus={shouldAutoFocus}
				className="min-w-0"
			/>
			<Input
				placeholder="Description (optional)"
				value={item.description || ''}
				onChange={handleDescriptionChange}
				onPressEnter={onEnterKey}
				className="min-w-0"
			/>
			<Select
				mode="multiple"
				placeholder={followUpActionsDisabled ? 'Set at task level' : 'Follow-up actions (optional)'}
				value={item.followUpActions || []}
				onChange={handleFollowUpActionsChange}
				options={FOLLOW_UP_ACTION_OPTIONS}
				className="min-w-0"
				maxTagCount="responsive"
				disabled={followUpActionsDisabled}
				title={
					followUpActionsDisabled
						? 'Follow-up actions are set at the task level. Clear them to set per-option actions.'
						: ''
				}
			/>
			<DeleteOutlined
				translate=""
				onClick={handleDelete}
				className="hidden cursor-pointer text-character-secondary group-hover:inline-block"
			/>
		</div>
	);
};

const MultipleChoicesField: FC<CustomCellProps> = ({ value, row, onChange }) => {
	const enabled = row.questionType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE;
	const items = useMemo(() => normalizeChoices(value), [value]);
	// Per-option and task-level follow-up actions are mutually exclusive. When the
	// task already carries task-level follow-ups, lock the per-option selects so the
	// two levels can't both be set. The walkthrough utils own the "do they exist?"
	// decision; this cell only flips enabled/disabled.
	const followUpActionsDisabled = useMemo(
		() => taskLevelFollowUpActionsExist(nullSafeGet('followUpActions', row)),
		[row]
	);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalItems, setModalItems] = useState<MultipleChoice[]>([]);
	// Mirror modalItems so add-after-typed-Enter and close-after-edit can read
	// fresh values without dep churn on the callbacks.
	const modalItemsRef = useRef<MultipleChoice[]>([]);

	const writeModalItems = useCallback((next: MultipleChoice[]) => {
		modalItemsRef.current = next;
		setModalItems(next);
	}, []);

	const nonEmptyItems = useMemo(() => items.filter(hasNonEmptyValue), [items]);

	const summary = useMemo(() => nonEmptyItems.map(formatChoiceLabel).join('; '), [nonEmptyItems]);

	const handleOpenModal = useCallback(() => {
		const seed = items.length === 0 ? renumber([{ orderingId: 0, value: '' }]) : renumber(items);
		writeModalItems(seed);
		setModalOpen(true);
	}, [items, writeModalItems]);

	const handleCloseModal = useCallback(() => {
		const cleaned = renumber(modalItemsRef.current.filter(hasNonEmptyValue));
		// BE shape doesn't carry orderingId — it's only a React-key helper here.
		const forParent = cleaned.map(({ value: optionValue, description, followUpActions }) => {
			const base =
				description && description.trim().length > 0
					? { value: optionValue.trim(), description: description.trim() }
					: { value: optionValue.trim() };
			return Array.isArray(followUpActions) && followUpActions.length > 0
				? { ...base, followUpActions }
				: base;
		});
		onChange(forParent);
		setModalOpen(false);
	}, [onChange]);

	const handleUpdate = useCallback(
		(index: number, patch: Partial<MultipleChoice>) => {
			const current = modalItemsRef.current;
			const next = current.map((existingItem, idx) =>
				idx === index ? { ...existingItem, ...patch } : existingItem
			);
			writeModalItems(next);
		},
		[writeModalItems]
	);

	const handleAddChoice = useCallback(() => {
		const current = modalItemsRef.current;
		const next = renumber([...current, { orderingId: current.length, value: '' }]);
		writeModalItems(next);
	}, [writeModalItems]);

	const handleDelete = useCallback(
		(index: number) => {
			const current = modalItemsRef.current;
			const next = renumber(current.filter((_, idx) => idx !== index));
			writeModalItems(next);
		},
		[writeModalItems]
	);

	const triggerIcon =
		nonEmptyItems.length === 0 ? <PlusOutlined translate="" /> : <EditOutlined translate="" />;

	const modalNonEmptyCount = modalItems.filter(hasNonEmptyValue).length;

	return (
		<div className="flex h-full w-full items-center gap-2 overflow-hidden">
			<span
				className={`line-clamp-3 flex-1 whitespace-normal break-words ${
					summary ? 'text-character-primary' : 'text-character-secondary'
				}`}
				title={enabled ? summary : ''}
			>
				{enabled ? summary || 'No options' : ''}
			</span>
			<Button
				size="small"
				type="link"
				icon={triggerIcon}
				onClick={handleOpenModal}
				disabled={!enabled}
				aria-label={nonEmptyItems.length === 0 ? 'Add answer options' : 'Edit answer options'}
			/>
			<Modal
				visible={modalOpen}
				title={`Answer options (${modalNonEmptyCount})`}
				onCancel={handleCloseModal}
				width={760}
				footer={
					<Button type="primary" onClick={handleCloseModal}>
						Done
					</Button>
				}
				destroyOnClose
			>
				<div className="flex flex-col">
					<div
						className="footnoteMedium mb-2 grid items-center gap-2 text-character-secondary"
						style={{ gridTemplateColumns: '1fr 1fr 1fr 24px' }}
					>
						<span>Score</span>
						<span>Description</span>
						<span>Follow-up actions</span>
						<span />
					</div>
					{modalItems.map((item, index) => (
						<ChoiceRow
							key={item.orderingId}
							item={item}
							index={index}
							followUpActionsDisabled={followUpActionsDisabled}
							onUpdate={handleUpdate}
							onDelete={handleDelete}
							onEnterKey={handleAddChoice}
						/>
					))}
					<Button
						type="link"
						icon={<PlusOutlined translate="" />}
						onClick={handleAddChoice}
						className="mt-2 self-start pl-0"
					>
						Add option
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export const renderMultipleChoicesCell = (props: CustomCellProps): React.ReactNode => (
	<MultipleChoicesField {...props} />
);

export default MultipleChoicesField;
